/**
 * TvMeetingOverlay — a deliberately minimal STATUS layer the TV paints OVER the
 * (untouched) Jitsi iframe. The meeting video is Jitsi's, and Jitsi already draws
 * its own top status row (subject, recording, connection). The earlier full-width
 * header strip duplicated and CLASHED with that, so it is gone.
 *
 * The TV is a passive, Remote-driven display — Jitsi's own toolbar is hidden
 * (`toolbarButtons: []`) and the Remote owns every action (Leave is the Remote's
 * `HANG_UP`; the meeting also ends cleanly on Jitsi `readyToClose`). So the overlay
 * carries NO controls, only non-conflicting self-state in the bottom-left
 * (REC / muted / camera off), shown only when active. Prop-driven; pure status.
 */

import type { SpotTvPresence } from '@spaces/contracts';
import { Dot } from '../ui/components.js';
import { MicOff, VideoOff } from '../ui/icons.js';

export function TvMeetingOverlay(props: {
    presence: SpotTvPresence;
}): JSX.Element {
    const { presence } = props;
    const recording = Boolean(presence.recording);
    const audioMuted = Boolean(presence.audioMuted);
    const videoMuted = Boolean(presence.videoMuted);
    const hasStatus = recording || audioMuted || videoMuted;

    if (!hasStatus) {
        return <div className = "tv-overlay" />;
    }

    return (
        <div className = "tv-overlay">
            <div className = "tv-overlay__badge" data-testid = "tv-selfstate">
                { recording && (
                    <span className = "tv-status tv-status--rec" data-testid = "tv-recording">
                        <Dot kind = "rec" /> REC
                    </span>
                ) }
                { audioMuted && (
                    <span className = "tv-status"><MicOff /> Muted</span>
                ) }
                { videoMuted && (
                    <span className = "tv-status"><VideoOff /> Camera off</span>
                ) }
            </div>
        </div>
    );
}
